import { useState } from "react";
import { Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

interface FAQItem {
  question: string;
  answer: string;
  /** Fala da oferta de compra/trial: some com a venda fechada. */
  offer?: boolean;
}

const faqs: FAQItem[] = [
  {
    question: "O Dr. Weedy substitui meu julgamento clínico?",
    answer:
      "Não. O Dr. Weedy é um assistente clínico baseado em evidências. Toda decisão final de prescrição é e sempre será do médico. A plataforma fornece informações científicas atualizadas para embasar melhor sua decisão, não para substituí-la.",
  },
  {
    question: "Os dados dos meus pacientes são compartilhados?",
    answer:
      "Absolutamente não. A plataforma é 100% em conformidade com a LGPD. Você controla quais dados insere. Não vendemos, compartilhamos ou utilizamos dados clínicos para treinamento de modelos de IA.",
  },
  {
    question: "A base científica é atualizada com que frequência?",
    answer:
      "A indexação do PubMed e outras bases científicas ocorre de forma contínua. Novos estudos publicados são processados e disponibilizados em menos de 24 horas após publicação oficial.",
  },
  {
    question: "Preciso ser especialista em canabinoides para usar?",
    answer:
      "Não. O Dr. Weedy foi desenhado para médicos prescritores de qualquer especialidade com interesse em cannabis medicinal. A plataforma traduz a complexidade científica em linguagem clínica prática e acessível.",
  },
  {
    question: "Qual a diferença entre os agentes especializados?",
    answer:
      "Cada agente é um módulo de IA treinado em domínio específico: um para literatura científica, outro para farmacologia, outro para interações medicamentosas, etc. Eles trabalham simultaneamente de forma coordenada para gerar uma resposta clínica completa.",
  },
  {
    question: "Existe período de teste gratuito?",
    offer: true,
    answer:
      "Sim. Oferecemos 7 dias de acesso gratuito com 10 consultas incluídas. Você experimenta a plataforma completa antes de qualquer compromisso financeiro.",
  },
];

export default function FAQSection({ salesClosed = false }: { salesClosed?: boolean }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  function toggle(index: number) {
    setOpenIndex((prev) => (prev === index ? null : index));
  }

  return (
    <section className="relative z-10 px-6 md:px-12 pb-20 md:pb-28">
      <div className="text-center mb-14">
        <Typography
          sx={{
            color: "#FF8904",
            fontWeight: 700,
            fontSize: "0.8rem",
            letterSpacing: "0.15em",
            marginBottom: "12px",
          }}
        >
          FAQ
        </Typography>
        <Typography
          variant="h4"
          sx={{
            color: "#ffffff",
            fontWeight: 700,
            fontFamily: "Oxanium, sans-serif",
            letterSpacing: "-0.02em",
            fontSize: { xs: "1.5rem", md: "2rem" },
          }}
        >
          Perguntas frequentes
        </Typography>
      </div>

      <div className="max-w-3xl mx-auto flex flex-col gap-3">
        {faqs.filter((faq) => !(salesClosed && faq.offer)).map((faq, index) => {
          const open = openIndex === index;
          return (
            <div
              key={faq.question}
              className="rounded-2xl overflow-hidden transition-colors"
              style={{
                background: open
                  ? "linear-gradient(135deg, rgba(255, 137, 4, 0.1) 0%, rgba(26, 49, 60, 0.7) 100%)"
                  : "rgba(255,255,255,0.04)",
                border: open
                  ? "1px solid rgba(255, 137, 4, 0.4)"
                  : "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <button
                type="button"
                onClick={() => toggle(index)}
                aria-expanded={open}
                className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
              >
                <Typography
                  sx={{ color: "#ffffff", fontWeight: 700, fontSize: "0.95rem" }}
                >
                  {faq.question}
                </Typography>
                <KeyboardArrowDownIcon
                  sx={{
                    color: "rgba(255,255,255,0.6)",
                    flexShrink: 0,
                    transition: "transform 0.2s ease",
                    transform: open ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              </button>
              {open && (
                <div className="px-6 pb-5">
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.6)",
                      fontSize: "0.9rem",
                      lineHeight: 1.7,
                    }}
                  >
                    {faq.answer}
                  </Typography>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
